const buildUrl = (path, params = null) => {
	const url = new URL(path, window.location.origin);
	if (params) {
		for (const [key, value] of Object.entries(params)) {
			if (value !== null && value !== undefined) {
				if (Array.isArray(value)) {
					for (const v of value) {
						url.searchParams.append(key, v);
					}
				} else {
					url.searchParams.append(key, value);
				}
			}
		}
	}
	return url;
};

const fetchWithHeaders = async (url, options = {}) => {
	const headers = new Headers(options.headers || {});
	if (!headers.has("X-Requested-With")) {
		headers.set("X-Requested-With", "XMLHttpRequest");
	}
	return fetch(url, { ...options, headers });
};

const downloadBlob = (blob, filename) => {
	const objectUrl = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectUrl;
	link.download = filename;
	link.click();
	window.URL.revokeObjectURL(objectUrl);
};

export const getMissingDataSources = async (date) => {
	const url = buildUrl("api/report/missing-data-sources/", { date });
	const response = await fetchWithHeaders(url);
	return await response.text();
};

export const getMetrics = async (days = null, signal = null) => {
	const url = buildUrl("/api/metrics", days ? { days } : null);
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getDataSourceMissingDays = async (id, year, month, signal = null) => {
	const url = buildUrl("api/data-source/missing-days/", { id, year, month });
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getDataSourceStatus = async (id, signal = null) => {
	const url = buildUrl("/api/data-source/status/", { id });
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getSourcesStatus = async (signal) => {
	const url = buildUrl("/api/sources-status");
	const response = await fetchWithHeaders(url, { signal });
	return await response.text();
};

export const getUserReportExport = async (id, type, signal, options = {}) => {
	const url = new URL(`/api/user/report/${id}/export/${type}`, window.location.origin);
	if (options.compareWith) {
		url.searchParams.append("compare_with", options.compareWith);
		if (options.compareWith === "custom" && options.compareStart && options.compareEnd) {
			url.searchParams.append("compare_start", options.compareStart);
			url.searchParams.append("compare_end", options.compareEnd);
		}
	}
	if (options.currency && options.currency !== "EUR") {
		url.searchParams.append("currency", options.currency);
	}
	const response = await fetch(url, {
		headers: {
			"X-Requested-With": "XMLHttpRequest",
		},
		signal,
	});
	const filename = response.headers.get("Content-Disposition").split("filename=")[1];
	response.blob().then((blob) => downloadBlob(blob, filename));
};

export const getUserReportModal = async (id = null, signal = null) => {
	const url = buildUrl("/api/user/report/modal", id ? { id } : null);
	const response = await fetchWithHeaders(url, { signal });
	if (!response.ok) throw new Error(response.statusText);
	return await response.text();
};

export const getUserReportResult = async (id, signal = null, options = null) => {
	const url = buildUrl(`/api/user/report/${id}/execute`, options);
	const headers = options?.to_chart ? { "Cache-Control": "no-cache" } : {};
	const response = await fetchWithHeaders(url, { headers, signal });
	return await response.json();
};

export const getWebsiteDetails = async (id, isGlobal = false, signal = null, options = null) => {
	const endpoint = isGlobal ? "api/website/global-details" : `api/website/${id}/details`;
	const url = buildUrl(endpoint, options);
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getWebsiteDetailsExport = async (
	id,
	isGlobal = false,
	signal = null,
	options = null,
) => {
	const endpoint = isGlobal ? "api/website/global-details" : `/api/website/${id}/details`;
	const finalOptions = { export: "csv", ...options };
	const url = buildUrl(endpoint, finalOptions);
	const response = await fetchWithHeaders(url, { signal });
	const filename = response.headers.get("Content-Disposition").split("filename=")[1];
	response.blob().then((blob) => downloadBlob(blob, filename));
};

export const completeJob = async (id, csrfToken, signal = null) => {
	const url = buildUrl(`/api/scheduled_job/${id}/complete/`);
	const response = await fetchWithHeaders(url, {
		method: "POST",
		headers: { "X-CSRFToken": csrfToken },
		signal,
	});
	return await response.json();
};

export const cloneUserReport = async (id, csrfToken, signal = null) => {
	const url = buildUrl(`/api/user/report/${id}/clone`);
	const response = await fetchWithHeaders(url, {
		method: "POST",
		headers: { "X-CSRFToken": csrfToken, "Content-Type": "application/json" },
		signal,
	});
	return await response.json();
};

export const createOrUpdateUserReport = async (data, id = null, signal = null) => {
	if (id) {
		data.id = id;
	}
	const method = id ? "PUT" : "POST";
	const url = buildUrl("/api/user/report/modal");
	const response = await fetchWithHeaders(url, {
		method,
		headers: {
			"X-CSRFToken": data.csrfmiddlewaretoken,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
		signal,
	});
	return await response.json();
};

export const deleteUserReport = async (id, csrfToken, signal = null) => {
	const url = buildUrl("/api/user/report/modal", { id });
	const response = await fetchWithHeaders(url, {
		method: "DELETE",
		headers: { "X-CSRFToken": csrfToken },
		signal,
	});
	return await response.json();
};

export const getReportScheduleModal = async (reportId = null, scheduleId = null, signal = null) => {
	const params = {};
	if (reportId) params.report_id = reportId;
	if (scheduleId) params.id = scheduleId;
	const url = buildUrl("/api/user/report/schedule/modal", params);
	const response = await fetchWithHeaders(url, { signal });
	if (!response.ok) throw new Error(response.statusText);
	return await response.text();
};

export const getReportSchedules = async (reportId = null, signal = null) => {
	const url = buildUrl("/api/user/report/schedule", reportId ? { report_id: reportId } : null);
	const response = await fetchWithHeaders(url, { signal });
	if (!response.ok) throw new Error(response.statusText);
	return await response.json();
};

export const createReportSchedule = async (data, csrfToken = null, signal = null) => {
	const url = buildUrl("/api/user/report/schedule");
	const response = await fetchWithHeaders(url, {
		method: "POST",
		headers: {
			"X-CSRFToken": csrfToken || window._galada?.csrfToken,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
		signal,
	});
	return await response.json();
};

export const updateReportSchedule = async (id, data, csrfToken = null, signal = null) => {
	const url = buildUrl(`/api/user/report/schedule/${id}`);
	const response = await fetchWithHeaders(url, {
		method: "PUT",
		headers: {
			"X-CSRFToken": csrfToken || window._galada?.csrfToken,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(data),
		signal,
	});
	return await response.json();
};

export const deleteReportSchedule = async (id, csrfToken = null, signal = null) => {
	const url = buildUrl(`/api/user/report/schedule/${id}`);
	const response = await fetchWithHeaders(url, {
		method: "DELETE",
		headers: {
			"X-CSRFToken": csrfToken || window._galada?.csrfToken,
		},
		signal,
	});
	return await response.json();
};

export const toggleReportSchedule = async (id, csrfToken = null, signal = null) => {
	const url = buildUrl(`/api/user/report/schedule/${id}/toggle`);
	const response = await fetchWithHeaders(url, {
		method: "POST",
		headers: {
			"X-CSRFToken": csrfToken || window._galada?.csrfToken,
		},
		signal,
	});
	return await response.json();
};
export const reworkDataSource = async (id, payload, csrfToken, signal = null) => {
	const url = buildUrl(`/api/data-source/${id}/rework/`);
	const response = await fetchWithHeaders(url, {
		method: "POST",
		headers: { "X-CSRFToken": csrfToken },
		body: payload,
		signal,
	});
	return await response.json();
};

export const getReworkStatus = async (id, signal = null) => {
	const url = buildUrl(`/api/data-source/${id}/rework/status/`);
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getLastRework = async (id, signal = null) => {
	const url = buildUrl(`/api/data-source/${id}/rework/last/`);
	const response = await fetch(url, { signal });
	if (!response.ok) {
		throw new Error(`HTTP error! status: ${response.status}`);
	}
	return await response.json();
};

export const getAdPositionAnalytics = async (signal = null, options = null) => {
	const url = buildUrl("/api/positions/performance-analytics", options);
	const response = await fetchWithHeaders(url, { signal });
	return await response.json();
};

export const getAdPositionAnalyticsExport = async (signal = null, options = null) => {
	const url = new URL("/api/positions/performance-analytics", window.location.origin);
	const finalOptions = {
		export: "csv",
		...options,
	};
	if (finalOptions) {
		for (const option in finalOptions) {
			if (finalOptions[option] === null) {
				continue;
			}
			if (Array.isArray(finalOptions[option])) {
				for (const value of finalOptions[option]) {
					url.searchParams.append(option, value);
				}
			} else {
				url.searchParams.append(option, finalOptions[option]);
			}
		}
	}
	const response = await fetch(url, {
		headers: {
			"X-Requested-With": "XMLHttpRequest",
		},
		signal,
	});
	const filename = response.headers.get("Content-Disposition").split("filename=")[1];
	response.blob().then((blob) => downloadBlob(blob, filename));
};

export const getNegotiatedRevenue = async (signal = null, options = null) => {
	const url = buildUrl("/api/accounting/negotiated-revenue", options);
	const cacheControl = options?.to_chart ? "no-cache" : "max-age=60";
	const response = await fetchWithHeaders(url, {
		headers: { "Cache-Control": cacheControl },
		signal,
	});
	return await response.json();
};

export const getNegotiatedRevenueExport = async (signal = null, options = null) => {
	const finalOptions = { export: "xlsx", ...options };
	const url = buildUrl("/api/accounting/negotiated-revenue", finalOptions);
	const response = await fetchWithHeaders(url, { signal });
	const filename = response.headers.get("Content-Disposition").split("filename=")[1];
	response.blob().then((blob) => downloadBlob(blob, filename));
};

export const getReconciliationDialog = async (id, signal = null) => {
	const template = window._galada.reconciliation.dialogUrl;
	const response = await fetchWithHeaders(template.replace("/0/", `/${id}/`), { signal });
	if (!response.ok) {
		throw new Error(gettext("Impossibile caricare la riconciliazione."));
	}
	return await response.text();
};

const updateReconciliation = async (template, id, data, csrfToken, signal = null) => {
	const response = await fetchWithHeaders(template.replace("/0/", `/${id}/`), {
		body: data,
		headers: { "X-CSRFToken": csrfToken },
		method: "POST",
		signal,
	});
	// 403/500 responses render HTML, not JSON: never let the parse error mask the failure.
	const payload = await response.json().catch(() => null);
	if (!response.ok) {
		const error = new Error(payload?.message || gettext("Impossibile salvare la riconciliazione."));
		error.details = payload?.errors;
		throw error;
	}
	return payload;
};

export const updateReconciliationVerification = async (id, data, csrfToken, signal = null) =>
	updateReconciliation(
		window._galada.reconciliation.verificationUpdateUrl,
		id,
		data,
		csrfToken,
		signal,
	);

export const updateReconciliationInvoice = async (id, data, csrfToken, signal = null) =>
	updateReconciliation(window._galada.reconciliation.invoiceUpdateUrl, id, data, csrfToken, signal);
